// FoodService.js
//
// The food catalog, served by /admin/food-dictionary.
//
// It used to be /admin/food, backed by the legacy `foods` table. That table is gone —
// V20260829 rebuilt `food_dictionary` as a projection of it AT PRESERVED IDS, V20260828
// repointed every menu and history foreign key onto the catalog, and V20260834 dropped
// `foods` and `food_serving_sizes` for good. Because the ids were preserved, every food id
// a menu already holds resolves in the new catalog and nothing had to be remapped.
//
// THE PUBLIC SHAPE OF THIS MODULE IS DELIBERATELY UNCHANGED. Fifteen files import it — the
// food pages, the top-bar search, the V1 meal-plan dialogs and the V2 builder — and the
// translation from the catalog's DTO to the shape they already read happens here, once, in
// `adaptFood` below. Changing the contract instead would have meant fifteen simultaneous
// edits to prove one endpoint swap.
//
// Two things genuinely change, both improvements the caller gets for free:
//
//   * COMPANY FOODS ARE IN THE SAME RESULT. The catalog carries `company_id`, and the
//     endpoint returns the caller's company foods alongside the shared system ones in a
//     single ranked query. Anything that used to merge two sources can stop.
//   * FIBER IS REAL. The catalog records it as a micronutrient rather than a column, so
//     `nutritionValues.fiber` is now populated where the catalog knows it — and stays
//     undefined where it does not, because "not measured" is not the same claim as "zero".

import {
    adminFoodDictionaryControllerSearchFoods,
    adminFoodDictionaryControllerGetFood,
    adminFoodDictionaryControllerCreateFood,
    adminFoodDictionaryControllerUpdateFood,
    adminFoodDictionaryControllerDeleteFood,
    adminFoodDictionaryControllerAddServing,
    adminFoodDictionaryControllerUpdateServing,
    adminFoodDictionaryControllerRemoveServing,
} from '../api/generated';

const FIBER = 'FIBER';

/**
 * Fiber off a serving's micronutrient list, or undefined.
 *
 * Undefined rather than 0 on purpose: most of the catalog does not record fiber, and showing an
 * unknown as 0 would put a number on screen nobody measured — and total it into the menu's fiber
 * figure as though someone had.
 */
const fiberOf = (micronutrients) =>
    micronutrients?.find((n) => n?.code === FIBER)?.amount;

/** The catalog's nutrition plus fiber, in the flat shape every caller already reads. */
const nutrition = (values, micronutrients) => ({
    calories: values?.calories,
    protein: values?.protein,
    carbs: values?.carbs,
    fats: values?.fats,
    fiber: fiberOf(micronutrients),
});

/**
 * A catalog food in the shape the app has always consumed.
 *
 * The catalog names a serving `name`; the old API called it `servingName`, and every consumer —
 * the serving dropdowns, the V2 builder's unit select, the meal-plan dialogs — reads
 * `servingName`. Renaming it here is what keeps that true.
 *
 * `scope` is passed through so a caller can tell a company's own food from a shared one. That is
 * how the builder marks a food as "של החברה" now that both arrive together.
 */
const adaptFood = (food) => ({
    id: food?.id,
    name: food?.name,
    brand: food?.brand,
    barcode: food?.barcode,
    certified: food?.certified,
    scope: food?.scope,
    createdDate: food?.createdDate,
    nutritionValues: nutrition(food?.defaultServingNutrition, food?.defaultServingMicronutrients),
    // The default serving flattened onto the food, for the one caller that falls back to it when a
    // food has no serving list at all.
    servingSizeId: food?.defaultServingId,
    servingSizeName: food?.defaultServingName,
    availableServingSizes: (food?.servings ?? []).map((serving) => ({
        id: serving?.id,
        servingName: serving?.name,
        gramsPerServing: serving?.gramsPerServing,
        isDefault: serving?.isDefault,
        nutritionValues: nutrition(serving?.nutritionValues, serving?.micronutrients),
        // Passed through so the builder's micronutrient panel fills in; it already reads this field
        // defensively, waiting for a catalog that carries them. It does now.
        micronutrients: serving?.micronutrients,
    })),
});

const adaptPage = (page) => ({
    content: (page?.content ?? []).map(adaptFood),
    totalElements: page?.totalElements ?? 0,
    totalPages: page?.totalPages ?? 0,
    last: page?.last ?? true,
    number: page?.number ?? 0,
    size: page?.size ?? 0,
});

// Search foods with filters.
// Returns {data} where data is the adapted page, preserving the contract callers read
// (response.data.content / totalElements / totalPages).
//
// `nutritionType` is dropped: it filtered the legacy table's food_origin column, which the catalog
// does not have. It is accepted and ignored rather than rejected so no caller has to change; the
// result is a wider list, never a wrong one.
export const searchFoods = async (params = {}) => {
    try {
        const {data, error} = await adminFoodDictionaryControllerSearchFoods({
            query: {
                page: params.page || 0,
                size: params.size || 20,
                ...(params.keyword && params.keyword.trim() !== '' ? {keyword: params.keyword.trim()} : {}),
                ...(params.barcode ? {barcode: params.barcode} : {}),
                ...(params.ownOnly ? {ownOnly: true} : {}),
                ...(params.certified && params.certified !== 'null' && params.certified !== ''
                    ? {certified: params.certified}
                    : {}),
            },
        });
        if (error) {
            throw error;
        }
        return {data: adaptPage(data)};
    } catch (error) {
        console.error('Error searching foods:', error);
        return {
            success: false,
            message: error.response?.data?.message || "שגיאה בחיפוש מזונות",
            data: { content: [], totalElements: 0, totalPages: 0, last: true }
        };
    }
};

// Get all foods (convenience wrapper).
export const getFoods = async (page = 0, size = 20, keyword = '', nutritionType = null, certified = null) => {
    const {data, error} = await adminFoodDictionaryControllerSearchFoods({
        query: {
            page,
            size,
            ...(keyword && keyword.trim() !== '' ? {keyword: keyword.trim()} : {}),
            ...(certified && certified !== 'null' && certified !== '' ? {certified} : {}),
        },
    });
    if (error) {
        throw error;
    }
    return {data: adaptPage(data)};
};

// Get food by ID.
export const getFoodById = async (foodId) => {
    try {
        const {data, error} = await adminFoodDictionaryControllerGetFood({
            path: {id: foodId},
        });
        if (error) {
            throw error;
        }
        return {
            success: true,
            data: adaptFood(data)
        };
    } catch (error) {
        console.error('Error fetching food:', error);
        return {
            success: false,
            message: error.response?.data?.message || "שגיאה בטעינת המזון",
            data: null
        };
    }
};

// The catalog entry EXACTLY as the server models it — scope, servings, micronutrients and all.
//
// Deliberately not `adaptFood`: that renames fields for the menu builder's callers, and the food
// detail page renders the server's own shape. The company endpoint returns the same
// FoodDictionaryDto the system one does, which is what lets one page serve a company admin and a
// super-admin from different endpoints without branching on the payload.
export const getFoodEntry = async (foodId) => {
    try {
        const {data, error} = await adminFoodDictionaryControllerGetFood({path: {id: foodId}});
        if (error) {
            throw error;
        }
        return {success: true, data};
    } catch (error) {
        console.error('Error fetching food entry:', error);
        return {
            success: false,
            message: error.response?.data?.message || "שגיאה בטעינת המזון",
            data: null,
        };
    }
};

// Check if a barcode is already taken.
//
// The catalog has no dedicated check endpoint, so this asks the search for that exact barcode.
// It answers the same question the old /admin/food/check-barcode did and within the same scope:
// the search is company-scoped, so it reports a clash with a food this company can actually see
// rather than with someone else's private row.
export const checkBarcodeExists = async (barcode, excludeFoodId = null) => {
    try {
        const {data, error} = await adminFoodDictionaryControllerSearchFoods({
            query: {barcode, page: 0, size: 5},
        });
        if (error) {
            throw error;
        }
        const matches = (data?.content ?? []).filter((food) => food?.id !== excludeFoodId);
        return {
            success: true,
            exists: matches.length > 0
        };
    } catch (error) {
        console.error('Error checking barcode:', error);
        return {
            success: false,
            exists: false
        };
    }
};

// Check if a name is already taken. Same approach as the barcode check above; an exact,
// case-insensitive name match counts, so a food merely CONTAINING the term is not a clash.
export const checkNameExists = async (name, excludeFoodId = null) => {
    try {
        const {data, error} = await adminFoodDictionaryControllerSearchFoods({
            query: {keyword: name, page: 0, size: 20},
        });
        if (error) {
            throw error;
        }
        const wanted = (name ?? '').trim().toLowerCase();
        const matches = (data?.content ?? []).filter(
            (food) => food?.id !== excludeFoodId && (food?.name ?? '').trim().toLowerCase() === wanted
        );
        return {
            success: true,
            exists: matches.length > 0,
        };
    } catch (error) {
        console.error('Error checking name:', error);
        return {
            success: false,
            exists: false
        };
    }
};

// Create a new food.
//
// The catalog creates a food and its FIRST serving in one call — a food with no serving has no
// nutrition and cannot be put in a menu, so the two are not separable. The old API took a nested
// nutritionValues object; both shapes are accepted here so existing call sites are untouched.
//
// `servings` is the third shape: every serving in one call, each with its micronutrients. It is the
// only one that can carry fiber, which the catalog stores as a FIBER micronutrient rather than a
// column, and the server uses it in place of the flat fields whenever it is non-empty.
export const createFood = async (foodData = {}) => {
    try {
        const values = foodData.nutritionValues ?? foodData;
        const {data, error} = await adminFoodDictionaryControllerCreateFood({
            body: {
                name: foodData.name,
                brand: foodData.brand,
                barcode: foodData.barcode,
                servingName: foodData.servingName ?? foodData.defaultServingName ?? '100 גרם',
                gramsPerServing: foodData.gramsPerServing ?? 100,
                calories: values?.calories,
                protein: values?.protein,
                carbs: values?.carbs,
                fats: values?.fats,
                ...(foodData.servings?.length ? {servings: foodData.servings.map(servingBody)} : {}),
            },
        });
        if (error) {
            throw error;
        }
        return {
            success: true,
            data: adaptFood(data),
            message: "המזון נוצר בהצלחה"
        };
    } catch (error) {
        console.error('Error creating food:', error);
        return {
            success: false,
            message: error.response?.data?.message || "שגיאה ביצירת המזון"
        };
    }
};

// Update food metadata (name / brand / barcode). Nutrition lives on the servings.
export const updateFood = async (foodId, foodData = {}) => {
    try {
        const {data, error} = await adminFoodDictionaryControllerUpdateFood({
            path: {id: foodId},
            body: {
                name: foodData.name,
                brand: foodData.brand,
                barcode: foodData.barcode,
            },
        });
        if (error) {
            throw error;
        }
        return {
            success: true,
            data: adaptFood(data),
            message: "המזון עודכן בהצלחה"
        };
    } catch (error) {
        console.error('Error updating food:', error);
        return {
            success: false,
            message: error.response?.data?.message || "שגיאה בעדכון המזון"
        };
    }
};

// Delete a food.
export const deleteFood = async (foodId) => {
    try {
        const {data, error} = await adminFoodDictionaryControllerDeleteFood({
            path: {id: foodId},
        });
        if (error) {
            throw error;
        }
        return {
            success: true,
            data,
            message: "המזון נמחק בהצלחה"
        };
    } catch (error) {
        console.error('Error deleting food:', error);
        return {
            success: false,
            message: error.response?.data?.message || "שגיאה במחיקת המזון"
        };
    }
};

/** The catalog's serving body. Callers still send the old {servingName, nutritionValues, …}. */
const servingBody = (servingSizeData = {}) => ({
    servingName: servingSizeData.servingName,
    gramsPerServing: servingSizeData.gramsPerServing,
    isDefault: servingSizeData.isDefault,
    nutritionValues: servingSizeData.nutritionValues ?? {
        calories: servingSizeData.calories,
        protein: servingSizeData.protein,
        carbs: servingSizeData.carbs,
        fats: servingSizeData.fats,
    },
    ...(servingSizeData.micronutrients ? {micronutrients: servingSizeData.micronutrients} : {}),
});

// Add a serving size.
//
// These three now return the WHOLE food rather than the single serving, because that is what the
// catalog returns — and it is the more useful answer: adding a serving can change which one is
// the default, so a caller holding only the new row would be showing a stale list.
export const addServingSizeToFood = async (foodId, servingSizeData) => {
    try {
        const {data, error} = await adminFoodDictionaryControllerAddServing({
            path: {id: foodId},
            body: servingBody(servingSizeData),
        });
        if (error) {
            throw error;
        }
        return {
            success: true,
            data: adaptFood(data),
            message: "גודל המנה נוסף בהצלחה"
        };
    } catch (error) {
        console.error('Error adding serving size:', error);
        return {
            success: false,
            message: error.response?.data?.message || "שגיאה בהוספת גודל המנה"
        };
    }
};

// Update a serving size.
export const updateServingSizeForFood = async (foodId, servingSizeId, servingSizeData) => {
    try {
        const {data, error} = await adminFoodDictionaryControllerUpdateServing({
            path: {id: foodId, servingId: servingSizeId},
            body: servingBody(servingSizeData),
        });
        if (error) {
            throw error;
        }
        return {
            success: true,
            data: adaptFood(data),
            message: "גודל המנה עודכן בהצלחה"
        };
    } catch (error) {
        console.error('Error updating serving size:', error);
        return {
            success: false,
            message: error.response?.data?.message || "שגיאה בעדכון גודל המנה"
        };
    }
};

// Remove a serving size.
export const removeServingSizeFromFood = async (foodId, servingSizeId) => {
    try {
        const {data, error} = await adminFoodDictionaryControllerRemoveServing({
            path: {id: foodId, servingId: servingSizeId},
        });
        if (error) {
            throw error;
        }
        return {
            success: true,
            data: adaptFood(data),
            message: "גודל המנה הוסר בהצלחה"
        };
    } catch (error) {
        console.error('Error removing serving size:', error);
        return {
            success: false,
            message: error.response?.data?.message || "שגיאה בהסרת גודל המנה"
        };
    }
};

// Alias for backward compatibility
export const addServingSize = addServingSizeToFood;
export const updateServingSize = updateServingSizeForFood;
export const removeServingSize = removeServingSizeFromFood;
